<template>
    <!-- Prevent mousedown from transferring focus away from the activator text field
         so that blur (and therefore validation) only fires on genuine field exit. -->
    <v-date-picker
        v-model="computedDates"
        hide-header
        scrollable
        :min="min"
        :max="max"
        :first-day-of-week="firstDayOfWeek"
        show-week
        :weeks-in-month="weeksInMonth"
        color="primary"
        :multiple="range"
        next-icon="custom:next"
        prev-icon="custom:prev"
        @mousedown.prevent
        v-on="{
            'update:modelValue': complete,
        }"
    />
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
// aliased: avoids colliding with the local getDaysArrayForRange wrapper below
import { getDaysArray as getDaysArrayUtil } from '@/js/utils/date-range-utils';

const props = defineProps({
    dates: {
        type: [Array, String],
        required: true,
    },
    range: {
        type: [Number, String, Boolean],
        default: false,
    },
    min: String,
    max: String,
    fixedStartDate: {
        type: String,
        default: '',
        required: false,
    },
    makeReadOnly: {
        type: Boolean,
        default: false,
    },
    weeksInMonth: {
        type: String,
        default: 'dynamic',
        validator: value => ['dynamic', 'static'].includes(value),
    },
});
const emit = defineEmits(['complete']);

const store = useStore();

const firstDayOfWeek = computed(() => store.state.clientConfig.generalConfig.weekStartsWith);

// new date-picker uses Date objects instead of strings, it takes range of values
// https://vuetifyjs.com/en/getting-started/upgrade-guide/#v-date-picker
const getDaysArrayForRange = ([start, end]) => getDaysArrayUtil([start, end], store.moment);

const modelDates = ref(props.range ? getDaysArrayForRange(props.dates) : props.dates);

const computedDates = computed({
    get: () => modelDates.value,
    set: dates => {
        // handle case where we only set end date of range. e.g. when promotion has already started
        if (props.fixedStartDate && props.range) {
            modelDates.value = getDaysArrayForRange([props.fixedStartDate, dates[0]]);
        } else {
            modelDates.value = dates;
        }
    },
});

const complete = () => {
    if (!props.range) {
        // single date mode
        emit('complete', store.moment(modelDates.value).format('YYYY-MM-DD'));
        return;
    }
    // range mode
    // parent components expect start and end of the range
    const dates =
        modelDates.value.length <= 2
            ? modelDates.value
            : [modelDates.value[0], modelDates.value[modelDates.value.length - 1]];
    emit('complete', dates.map(date => store.moment(date).format('YYYY-MM-DD')));
};
</script>

<style lang="scss" scoped>
:deep(.v-date-picker-month__day--adjacent) {
    font-size: 1.2rem;
}

:deep(.v-date-picker-month) {
    .v-btn--icon.v-btn--density-default {
        width: 3rem !important;
        height: 3rem !important;
    }
}

:deep(.v-date-picker-month__day) {
    height: 3.2rem;
    width: 3.2rem;
}

:deep(.v-date-picker-month__weekday) {
    font-size: 1.2rem;
    color: rgba(0, 0, 0, 0.38);
}

.v-date-picker--show-week {
    width: auto;
    background-color: var(--v-background-base);
}

.v-date-picker-header__value button {
    font-size: 1.4rem;
}

:deep(.v-date-picker-controls) {
    padding-top: 0.8rem;

    .v-btn__content {
        font-weight: 800;
        font-size: 1.4rem !important;
    }
}
</style>
